import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const useEditarPregunta = ({ preguntaId }) => {
  const [pregunta, setPregunta] = useState({
    id: null,
    tema_id: null,
    nivel_id: null,
    texto_pregunta: "",
    tipo_pregunta: "opcion_multiple",
    limite_tiempo: 60,
    retroalimentacion: "",
    respuestas: [
      { id: null, texto_respuesta: "", es_correcta: false },
      { id: null, texto_respuesta: "", es_correcta: false },
      { id: null, texto_respuesta: "", es_correcta: false },
      { id: null, texto_respuesta: "", es_correcta: false },
    ],
  });

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Cargar la pregunta existente
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const headers = { headers: { Authorization: `Bearer ${token}` } };

    (async () => {
      try {
        setCargando(true);
        const resp = await axios.get(
          `${API_BASE_URL}/api/preguntas/${preguntaId}`,
          headers
        );

        const data = resp.data || {};
        const tipo = data.tipo_pregunta || "opcion_multiple";

        // Algunos backends devuelven 'respuestas' o 'opciones'
        let respuestasApi = Array.isArray(data.respuestas)
          ? data.respuestas
          : Array.isArray(data.opciones)
          ? data.opciones
          : [];

        let respuestas = respuestasApi.map((r) => ({
          id: r.id ?? null,
          texto_respuesta: r.texto_respuesta ?? r.texto ?? "",
          es_correcta: !!r.es_correcta,
        }));

        if (tipo === "verdadero_falso") {
          respuestas = respuestas.slice(0, 2);
          while (respuestas.length < 2) {
            respuestas.push({
              id: null,
              texto_respuesta: respuestas.length === 0 ? "Verdadero" : "Falso",
              es_correcta: false,
            });
          }
        } else {
          respuestas = respuestas.slice(0, 4);
          while (respuestas.length < 4) {
            respuestas.push({
              id: null,
              texto_respuesta: "",
              es_correcta: false,
            });
          }
        }

        setPregunta({
          id: data.id ?? null,
          tema_id: data.tema_id ?? null,
          nivel_id: data.nivel_id ?? null,
          texto_pregunta: data.texto_pregunta || "",
          tipo_pregunta: tipo,
          limite_tiempo: data.limite_tiempo ?? 60,
          retroalimentacion: data.retroalimentacion ?? "",
          respuestas,
        });
      } catch (e) {
        console.error("No se pudo cargar la pregunta:", e);
        Swal.fire("Error", "No se pudo cargar la pregunta.", "error");
      } finally {
        setCargando(false);
      }
    })();
  }, [preguntaId]);

  // Mantener coherencia de respuestas al cambiar el tipo
  useEffect(() => {
    setPregunta((prev) => {
      const tipo = prev.tipo_pregunta;
      let respuestas;
      if (tipo === "verdadero_falso") {
        respuestas = [
          {
            id: prev.respuestas?.[0]?.id ?? null,
            texto_respuesta: "Verdadero",
            es_correcta: prev.respuestas?.[0]?.es_correcta ?? false,
          },
          {
            id: prev.respuestas?.[1]?.id ?? null,
            texto_respuesta: "Falso",
            es_correcta: prev.respuestas?.[1]?.es_correcta ?? false,
          },
        ];
      } else {
        respuestas = Array.from({ length: 4 }, (_, i) => ({
          id: prev.respuestas?.[i]?.id ?? null,
          texto_respuesta: prev.respuestas?.[i]?.texto_respuesta ?? "",
          es_correcta: prev.respuestas?.[i]?.es_correcta ?? false,
        }));
      }
      return { ...prev, respuestas };
    });
  }, [pregunta.tipo_pregunta]);

  const handleChange = (e) => {
    setPregunta((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleRespuestaChange = (index, e) => {
    setPregunta((p) => {
      const respuestas = [...p.respuestas];
      if (e.target.name === "es_correcta") {
        respuestas.forEach((r, i) => (r.es_correcta = i === index));
      } else {
        respuestas[index].texto_respuesta = e.target.value;
      }
      return { ...p, respuestas };
    });
  };

  const guardarCambios = async (e) => {
    e?.preventDefault?.();

    if (!pregunta.respuestas.some((r) => r.es_correcta)) {
      alert("Debe marcar al menos una respuesta como correcta.");
      return false;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No se encontró token. Inicia sesión nuevamente.");
        return false;
      }

      setGuardando(true);

      const payload = {
        id: pregunta.id,
        tema_id: pregunta.tema_id || null,
        nivel_id: pregunta.nivel_id || null,
        texto_pregunta: pregunta.texto_pregunta,
        tipo_pregunta: pregunta.tipo_pregunta,
        limite_tiempo: Number(pregunta.limite_tiempo) || 60,
        retroalimentacion: pregunta.retroalimentacion || "",
        respuestas: pregunta.respuestas.map((r) => ({
          id: r.id ?? undefined, // si existe, ayuda al backend a actualizar
          texto_respuesta: r.texto_respuesta,
          es_correcta: !!r.es_correcta,
        })),
      };

      await axios.put(`${API_BASE_URL}/api/preguntas/${pregunta.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "Pregunta actualizada correctamente",
        timer: 1200,
        showConfirmButton: false,
      });

      return true;
    } catch (error) {
      console.error(
        "Error al actualizar pregunta:",
        error?.response?.data || error.message
      );
      Swal.fire("Error", "No se pudo actualizar la pregunta.", "error");
      return false;
    } finally {
      setGuardando(false);
    }
  };

  return {
    pregunta,
    setPregunta,
    cargando,
    guardando,
    handleChange,
    handleRespuestaChange,
    guardarCambios,
  };
};

export default useEditarPregunta;
